import { getRegion } from '@adsk/acs-sc-web-platform-api';
import { createMonitoringListener } from '@adsk/exo-skeleton-api';

import { setDynatraceUser, sendEventToDynatrace } from './dynatrace';
import { hasOverrides, getRemoteOverrides } from './getRemoteOverrides';
import {
  deleteFromDTWindowMonitoringContext,
  setToDTWindowMonitoringContext,
} from './monitoringContext';

// DT doesn't support properties with dash in the key name
export const VISIBLE_REMOTES_METADATA_KEY = 'exo_visible_remotes_metadata';
const CURRENT_TOOL_SCOPE_KEY = 'exo_current_tool_scope';
const VERSION_KEY = 'exo_version';
const BUILD_IDENTIFIER_KEY = 'exo_build_identifier';

let monitoringInitialized = false;
let monitoringListener:
  | {
      dispose(): void;
    }
  | undefined;

export interface EventContext {
  [key: string]: unknown;
  scope?: string;
}

interface User {
  id?: string;
  analyticsId?: string | null;
  email?: string | null;
}

export function setUserToErrorContext(user: User) {
  setToDTWindowMonitoringContext('has_user', true);
  setToDTWindowMonitoringContext('user_email', user?.email);

  if (user.analyticsId) {
    setDynatraceUser(user.analyticsId);
    setToDTWindowMonitoringContext('analytics_id', user.analyticsId);
  }
}

export function setOverrideToErrorContext() {
  if (hasOverrides()) {
    setToDTWindowMonitoringContext('override_details', getRemoteOverrides());
  }
}

export function setIsUserOfflineToErrorContext(isUserOffline: boolean) {
  setToDTWindowMonitoringContext('is_user_offline', isUserOffline);
}

export function removeUserFromErrorContext() {
  deleteFromDTWindowMonitoringContext('user_email');
  deleteFromDTWindowMonitoringContext('has_user');
  deleteFromDTWindowMonitoringContext('analytics_id');
}

export function disposeMonitoringContext() {
  if (!monitoringInitialized) {
    return;
  }

  removeUserFromErrorContext();
}

export function setCurrentToolScopeToContext(scope: string) {
  setToDTWindowMonitoringContext(CURRENT_TOOL_SCOPE_KEY, scope);
}

export function removeCurrentToolScopeFromContext() {
  deleteFromDTWindowMonitoringContext(CURRENT_TOOL_SCOPE_KEY);
}

let visibleRemotes: Record<string, Record<string, { visible: boolean }>> = {};

export function setVisibleRemoteContext(scope: string, moduleName: string) {
  if (!visibleRemotes[scope]) {
    visibleRemotes[scope] = {};
  }

  visibleRemotes[scope][moduleName] = { visible: true };

  setToDTWindowMonitoringContext(VISIBLE_REMOTES_METADATA_KEY, visibleRemotes);
}

export function removeVisibleRemoteContext(scope: string, moduleName: string) {
  if (!visibleRemotes[scope]) {
    return;
  }

  delete visibleRemotes[scope][moduleName];

  if (Object.keys(visibleRemotes[scope]).length === 0) {
    delete visibleRemotes[scope];
  }

  setToDTWindowMonitoringContext(VISIBLE_REMOTES_METADATA_KEY, visibleRemotes);
}

export function setProjectToContext({ projectId }: { projectId: string }) {
  setToDTWindowMonitoringContext('project_id', projectId);
}

export function removeProjectFromContext() {
  deleteFromDTWindowMonitoringContext('project_id');
}

export function setAccountToContext({
  accountId,
}: {
  accountId: string;
  accountName?: string | null;
}) {
  setToDTWindowMonitoringContext('account_id', accountId);
}

export function removeAccountFromContext() {
  deleteFromDTWindowMonitoringContext('account_id');
}

export function resetVisibleRemoteContext() {
  visibleRemotes = {};
  deleteFromDTWindowMonitoringContext(VISIBLE_REMOTES_METADATA_KEY);
}

export function disposeMonitoring() {
  if (monitoringInitialized) {
    monitoringInitialized = false;

    if (monitoringListener) {
      monitoringListener.dispose();
    }
  }
}

export function initializeMonitoringHandler() {
  if (!monitoringInitialized) {
    monitoringInitialized = true;

    monitoringListener = createMonitoringListener({
      onReport({ data: { context, scope } }) {
        // we ignore type and value because we want the consumers to be independent
        sendEventToDynatrace({
          context,
          scope,
        });
      },
    });
  }
}

export function initializeMonitoringMetadata() {
  setToDTWindowMonitoringContext('region', getRegion());

  const version = process.env.APPLICATION_VERSION;
  const buildIdentifier = process.env.APPLICATION_UNIQUE_BUILD_ID;

  if (!version) {
    throw new Error(
      'APPLICATION_VERSION is not defined. Please check your webpack config.',
    );
  }

  if (!buildIdentifier) {
    throw new Error(
      'APPLICATION_UNIQUE_BUILD_ID is not defined. Please check your webpack config.',
    );
  }

  setToDTWindowMonitoringContext(VERSION_KEY, version);
  setToDTWindowMonitoringContext(BUILD_IDENTIFIER_KEY, buildIdentifier);
}

export const REPORT_EVENT_TYPES = {
  REMOTE_MODULE_ERROR: 'remote-module-error',
  ROOT_ERROR: 'root-error-boundary',
  PAGE_NOT_FOUND: 'PAGE_NOT_FOUND',
  DEPRECATED_ROUTE_OR_REDIRECT: 'DEPRECATED_ROUTE_OR_REDIRECT',
};
